<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>
		<breadcrumbs
			:breadcrumbs="page.breadcrumbs"
			:class="page.headingImage ? '' : 'no-slider'"
			:home="defaults[locale].website.hotelName"
		/>
		<main-content
			:title="page.title || page.header"
			:content="page.content"
			:booking-id="parseInt(page.bookingID, 10)"
		>
			<template #content>
				<div class="columns column6">
					<h1>{{ page.title || page.header }}</h1>
					<div class="room-icons">
						<div v-if="page.pax" class="room-icon">
							<font-awesome-icon :icon="`fal fa-users`" size="1x" />
							<span>{{ page.pax }}</span>
						</div>
						<div v-if="page.surface" class="room-icon">
							<font-awesome-icon :icon="`fal fa-ruler-combined`" size="1x" />
							<span>{{ page.surface }}</span>
						</div>
					</div>
				</div>
			</template>
		</main-content>

		<section class="main-content background-color align-left">
			<div class="row">
				<div class="columns column12">
					<div v-parse-links v-html="page.extraContent" />
				</div>
			</div>
		</section>

		<client-only>
			<carousel
				:items-to-show="1"
				:wrap-around="true"
				:pagination-enabled="false"
				:autoplay="7000"
				:pause-autoplay-on-hover="false"
				:transition="1500"
				class="slides"
			>
				<slide v-for="(item, index) in page.images" :key="index">
					<div class="slide">
						<picture>
							<source
								v-if="item.imagesWebp"
								:srcset="item.imagesWebp[1]"
								media="(max-width: 500px)"
								type="image/webp"
							/>
							<source v-if="item.imagesWebp" :srcset="item.imagesWebp[0]" type="image/webp" />
							<source :srcset="item.images[1]" media="(max-width: 500px)" />
							<source :srcset="item.images[0]" />
							<img :src="item.image" :alt="item.imageAlt" loading="lazy" />
						</picture>
					</div>
				</slide>

				<template #addons>
					<pagination />
				</template>
			</carousel>
		</client-only>

		<section class="main-content align-center">
			<div class="row">
				<div class="columns column12">
					<h2>{{ defaults[locale].rooms.header }}</h2>
					<nuxt-link class="button button-no-fill" :to="localePath('rooms')">
						{{ defaults[locale].rooms.more }}
					</nuxt-link>
				</div>
			</div>
			<div class="row promotions-row flex-row slide-row">
				<div
					v-for="room in roomsData.filter((item) => item.pageID !== page.pageID)"
					:key="room.pageID"
					class="block flex-item"
				>
					<div class="block-img">
						<nuxt-link :to="localePath(`/${room.filename}`)">
							<picture>
								<source
									v-if="room.imageOverviewWebp"
									:srcset="room.imageOverviewWebp"
									type="image/webp"
								/>
								<source :srcset="room.imageOverview" />
								<img :src="room.imageOverview" :alt="room.imageOverviewAlt" loading="lazy" />
							</picture>
						</nuxt-link>
					</div>
					<div class="block-content">
						<h3>{{ room.header }}</h3>
						<nuxt-link :to="localePath(`/${room.filename}`)" class="button button-no-fill">
							{{ defaults[locale].rooms.view }}
						</nuxt-link>
					</div>
				</div>
			</div>
		</section>

		<contentblocks v-if="page.contentblocks && page.contentblocks.length" :items="page.contentblocks" />

		<review-slider v-if="page.showReviewSlider" :header="defaults[locale].website.reviewsHeader" />
		<frequently-asked-questions
			v-if="page.frequentlyAskedQuestion"
			:header="page.frequentlyAskedQuestion.header"
			:items="page.frequentlyAskedQuestion.faqitems"
		/>
		<pre-footer v-if="page.footerSection.length" :footer-section="page.footerSection[0]" />
		<newsletter
			v-if="page.showNewsletter"
			:header="defaults[locale].website.newsletterHeader"
			:content="defaults[locale].website.newsletterContent"
			:button="defaults[locale].website.newsletterButton"
		/>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localePath = useLocalePath();

await fetchPage();
await fetchDefaults();

const { data: roomsData } = await useWebsiteFetch('rooms', {
	query: { language: locale.value },
	key: `${locale.value}/rooms`,
});
</script>

<style lang="scss" scoped>
.main-content {
	:deep(ul) {
		padding: 0 0 0 20px;
	}

	:deep(li) {
		width: 50%;
		float: left;
		padding: 0 50px 0 0;
	}
}

.room-icons {
	display: flex;
	flex-flow: wrap row;
	justify-content: flex-start;
	align-items: center;
	gap: 10px;
	margin: 20px 0;
	width: 100%;

	.room-icon {
		margin: 0 20px 0 0;
	}

	span {
		font-size: 12px;
	}

	svg {
		color: $cta-color;
	}
}

.slides .slide {
	width: 100%;
}

/* stylelint-disable selector-class-pattern */
:deep(.carousel__track) {
	height: 100%;
}

:deep(.carousel__pagination) {
	position: relative;
	margin-top: -50px;
}

:deep(.carousel__pagination-item) {
	margin: 0 10px;

	button {
		width: 11px;
		height: 11px;
		border-radius: 50%;
		background: rgb(239 239 239);

		&.carousel__pagination-button--active {
			background: $cta-color;
		}

		&::after {
			display: none;
		}
	}
}
/* stylelint-enable selector-class-pattern */
</style>
